import { CaretDownIcon, CaretUpDownIcon, CaretUpIcon } from '@phosphor-icons/react';

import { TableHead } from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { DECISION_TEXT, TABLE_HEAD_CELL } from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';

export type SortDirection = 'asc' | 'desc';

/**
 * A sortable (or plain-label) table header cell, generic over the caller's
 * own sort-key union. Originally built for the paid campaigns table
 * (`CampaignTableHead`) and generalized here so any table — campaigns,
 * posts, anything else — shares one sort-header component instead of
 * re-deriving the same caret/tooltip/hit-target behavior per table.
 *
 * Three things were wrong with the hand-rolled headers this replaces, and all
 * three were invisible until measured against the same table on marketer.com:
 *
 * 1. **They wrapped.** `Conversion value` broke to two lines, so the header
 *    band was double height on a page whose whole job is dense scanning.
 * 2. **Sorting was unguessable.** Every metric header was already a working
 *    sort button, but the caret only appeared AFTER you had sorted — so the
 *    affordance was visible exclusively to someone who had already found it.
 *    A permanent low-contrast `⇅` says "this sorts" before the first click.
 * 3. **The sort target was smaller than it looked.** The button FILLS the cell
 *    (`flex h-8 w-full`) rather than shrinking to its label: at `text-detail`
 *    the label's own line box is ~16px, so a naturally-sized button is a
 *    sub-24px hit target inside a 32px header — it reads as clickable across
 *    the whole cell and only actually responds on the text.
 * 4. **Nothing said what the numbers meant.** A dotted underline is the
 *    long-standing convention for "there is a definition behind this", and it
 *    costs no width in a table where width is the scarce resource.
 */
export function SortableTableHead<K extends string>({
  label,
  definition,
  sortKey,
  activeSort,
  direction,
  onSort,
  align = 'right',
  className,
  decisionText = false,
}: {
  label: string;
  definition?: string;
  sortKey?: K;
  activeSort?: K | null;
  direction?: SortDirection;
  onSort?: (key: K) => void;
  align?: 'left' | 'right';
  className?: string;
  /** Prints the label at 13px and gives the sort button a 44px target below md, for a table people decide from. */
  decisionText?: boolean;
}) {
  const sortable = sortKey !== undefined && onSort !== undefined;
  const active = sortable && activeSort === sortKey;
  const SortIcon = !active ? CaretUpDownIcon : direction === 'asc' ? CaretUpIcon : CaretDownIcon;

  const text = definition ? (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          tabIndex={0}
          // No `rounded-*` here: a border-radius on a box with only a
          // `border-b` still curves the border's painted path in at both
          // ends to follow the (invisible) corner radius, so a dashed
          // bottom border renders as a bow, not a straight line — visible
          // under "Reach" in the style guide's `DataTableShowcase`, 2026-09-06.
          className="cursor-help border-b border-dashed border-current/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {label}
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-[19rem] text-pretty">{definition}</TooltipContent>
    </Tooltip>
  ) : (
    <span>{label}</span>
  );

  // THE GLYPH FOLLOWS THE LABEL, on both alignments.
  //
  // It used to PRECEDE a right-aligned label, for a real reason: with the icon
  // after it, "Budget" ends one glyph-width short of the dollar amounts below,
  // even though header and cells are both `text-right`. That reasoning was
  // sound about alignment and wrong about reading. A caret sitting to the LEFT
  // of a header reads as a disclosure triangle — "expand this column" — which
  // is exactly how the founder read it on 2026-08-23 (_"I'm confused why the
  // caret is before it instead of after it. That's confusing."_), and it is
  // the same glyph this app uses for disclosure everywhere else.
  //
  // Sort-after-label is also the near-universal convention (shadcn's own data
  // table, Linear, Google Ads). The icon stays mounted at `opacity-0` rather
  // than being unmounted, so nothing shifts when a column becomes active.
  //
  // On a right-aligned column the glyph is taken OUT of the flow and hangs in
  // the cell's own right padding (12px, exactly the glyph's width), so the label
  // ends where the numbers below it end. Reserving the glyph's width in the flow
  // is what kept every numeric header ~16px short of its values.
  const hanging = align === 'right';
  const content = (
    <span className={cn('items-center', hanging ? 'relative inline-flex' : 'inline-flex gap-1')}>
      {text}
      {sortable && (
        <SortIcon
          className={cn(
            'size-3 shrink-0 transition-opacity',
            hanging && 'absolute left-full top-1/2 -translate-y-1/2',
            active ? 'opacity-100' : 'opacity-0 group-hover/th:opacity-50',
          )}
          aria-hidden="true"
        />
      )}
    </span>
  );

  return (
    <TableHead
      className={cn(
        // `md:px-4` from the primitive is generous for prose and wasteful for a
        // numeric grid; `px-2` keeps more columns on screen without crowding.
        'group/th whitespace-nowrap font-medium',
        decisionText ? DECISION_TEXT : 'text-detail',
        TABLE_HEAD_CELL,
        align === 'right' ? 'text-right' : 'text-left',
        className,
      )}
      aria-sort={
        !sortable ? undefined : active ? (direction === 'asc' ? 'ascending' : 'descending') : 'none'
      }
    >
      {sortable ? (
        <button
          type="button"
          className={cn(
            decisionText ? 'h-11 md:h-8' : 'h-8',
            'flex w-full items-center rounded-sm text-inherit outline-none transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring',
            align === 'right' ? 'justify-end' : 'justify-start',
          )}
          onClick={() => onSort(sortKey)}
        >
          {content}
        </button>
      ) : (
        content
      )}
    </TableHead>
  );
}
